import { LATIN_FONT_OPTIONS, ARABIC_FONT_OPTIONS, DISPLAY_FONT_OPTIONS } from "./font-catalog";
export { SYSTEM_SERIF_FONT, SYSTEM_SERIF_STACK, LATIN_FONT_OPTIONS, ARABIC_FONT_OPTIONS, SECTION_VARIANT_FONTS, PRODUCT_CARD_FONTS, DISPLAY_FONT_OPTIONS } from "./font-catalog";
const colorFields = [
    {
        id: "primary",
        type: "color",
        label: { ar: "اللون الأساسي", en: "Primary color" },
        helpText: {
            ar: "لون الأزرار الأساسية وأزرار الإجراء.",
            en: "Used for primary buttons and call-to-action elements.",
        },
        default: "#F5A524",
    },
    {
        id: "accent",
        type: "color",
        label: { ar: "اللون المميز", en: "Accent color" },
        helpText: {
            ar: "لون مميز للروابط والشارات.",
            en: "Highlight color for links and badges.",
        },
        default: "#0B1220",
    },
    {
        id: "surface",
        type: "color",
        label: { ar: "لون الخلفية", en: "Surface color" },
        helpText: {
            ar: "الخلفية الأساسية للصفحات والكروت.",
            en: "Default background for page surfaces and cards.",
        },
        default: "#F7F4EE",
    },
    {
        id: "ink",
        type: "color",
        label: { ar: "لون النص", en: "Text color" },
        helpText: {
            ar: "اللون الافتراضي للنصوص.",
            en: "Default text color.",
        },
        default: "#0B1220",
    },
    // Section-background variants that AREN'T one of the four roles above. Section
    // backgrounds ("Section background" slot) offer Page/Subtle/Primary/Accent/Dark;
    // Page=Surface, Primary=Primary, Accent=Accent are already editable roles, but
    // Subtle and Dark were only DERIVED (Subtle from Surface, Dark from Text), so a
    // seller couldn't choose them. These make them editable. NO default on purpose:
    // empty = "derive it" (Subtle ← Surface, Dark ← Text), so existing stores are
    // unchanged until a seller sets a colour.
    {
        id: "surfaceSubtle",
        type: "color",
        label: { ar: "خلفية القسم الفاتحة", en: "Subtle background" },
        helpText: {
            ar: "خلفية «فاتحة مختلفة» للأقسام. سيبها فاضية عشان تتحسب أوتوماتيك من لون الخلفية.",
            en: 'The "Subtle" section background. Leave empty to derive it from your Surface color.',
        },
    },
    {
        id: "surfaceDark",
        type: "color",
        label: { ar: "خلفية القسم الغامقة", en: "Dark background" },
        helpText: {
            ar: "خلفية «غامقة» للأقسام. سيبها فاضية عشان تاخد لون النص.",
            en: 'The "Dark" section background. Leave empty to use your Text color.',
        },
    },
    // The PAGE GROUND, distinct from `surface`. See `ThemeTokens.colors.pageGround`
    // for why the two are separate roles. NO default on purpose: empty = "the
    // ground IS the surface colour", so every store authored before this field is
    // unchanged.
    {
        id: "pageGround",
        type: "color",
        label: { ar: "خلفية الصفحة", en: "Page background" },
        helpText: {
            ar: "لون الورقة اللي الصفحة كلها قاعدة عليه. سيبه فاضي عشان ياخد لون الخلفية.",
            en: "The ground the whole page sits on, behind every section. Leave empty to use your Surface color.",
        },
    },
    // The FOOTER BAND and its ink. See `ThemeTokens.colors.footerSurface`. All
    // three are empty by default: absent ⇒ the footer variant keeps the band and
    // foreground it always had.
    {
        id: "footerSurface",
        type: "color",
        label: { ar: "خلفية التذييل", en: "Footer background" },
        helpText: {
            ar: "لون شريط التذييل. سيبه فاضي عشان ياخد لون التصميم الافتراضي.",
            en: "The footer band color. Leave empty to keep the footer style's own band.",
        },
    },
    {
        id: "footerInk",
        type: "color",
        label: { ar: "لون نص التذييل", en: "Footer text color" },
        helpText: {
            ar: "سيبه فاضي عشان يتحسب أوتوماتيك من خلفية التذييل (فاتحة ولا غامقة).",
            en: "Leave empty to derive it from the footer background's lightness.",
        },
    },
    {
        id: "footerHeading",
        type: "color",
        label: { ar: "لون عناوين التذييل", en: "Footer heading color" },
        helpText: {
            ar: "سيبه فاضي عشان العناوين تاخد نفس لون نص التذييل.",
            en: "Leave empty for headings to take the footer text color.",
        },
    },
];
const typographyFields = [
    {
        id: "headingEn",
        type: "select",
        label: { ar: "خط العناوين (إنجليزي)", en: "Heading font (English)" },
        options: LATIN_FONT_OPTIONS,
        default: "Inter",
    },
    {
        id: "headingAr",
        type: "select",
        label: { ar: "خط العناوين (عربي)", en: "Heading font (Arabic)" },
        options: ARABIC_FONT_OPTIONS,
        default: "Cairo",
    },
    {
        id: "bodyEn",
        type: "select",
        label: { ar: "خط النصوص (إنجليزي)", en: "Body font (English)" },
        options: LATIN_FONT_OPTIONS,
        default: "Inter",
    },
    {
        id: "bodyAr",
        type: "select",
        label: { ar: "خط النصوص (عربي)", en: "Body font (Arabic)" },
        options: ARABIC_FONT_OPTIONS,
        default: "Cairo",
    },
    // The DISPLAY face — hero titles + editorial H2s. Empty (no default) means
    // "same as the heading font", which is what every store did before this field.
    {
        id: "displayEn",
        type: "select",
        label: { ar: "خط العناوين الكبيرة (إنجليزي)", en: "Display font (English)" },
        helpText: {
            ar: "خط عناوين الهيرو والأقسام التحريرية. سيبه فاضي عشان ياخد خط العناوين.",
            en: "Face for hero titles and editorial headings. Leave empty to use the heading font.",
        },
        options: DISPLAY_FONT_OPTIONS,
    },
    {
        id: "displayAr",
        type: "select",
        label: { ar: "خط العناوين الكبيرة (عربي)", en: "Display font (Arabic)" },
        helpText: {
            ar: "سيبه فاضي عشان ياخد خط العناوين العربي.",
            en: "Leave empty to use the Arabic heading font.",
        },
        options: ARABIC_FONT_OPTIONS,
    },
];
/**
 * The product-card design options — the SINGLE source of truth for the card
 * "drawing" choices. Referenced by the theme-global `productCardStyle` field
 * below AND by the per-section override (`productCardStyleField()` in
 * `sections/_shared.ts`), so the two never drift. Add a card style ONCE here and
 * both the store-wide picker and every per-section override offer it.
 */
export const PRODUCT_CARD_STYLE_OPTIONS = [
    { value: "grid", label: { ar: "كلاسيكي", en: "Classic" } },
    { value: "clean", label: { ar: "بسيط", en: "Minimal" } },
    { value: "overlay", label: { ar: "تراكب", en: "Overlay" } },
    { value: "compact", label: { ar: "مدمج", en: "Compact" } },
    { value: "card", label: { ar: "بطاقة مع زر", en: "Card" } },
    { value: "simple", label: { ar: "مجرد", en: "Simple" } },
    { value: "soft-tile", label: { ar: "بلاطة ناعمة", en: "Soft tile" } },
    { value: "editorial", label: { ar: "تحريري", en: "Editorial" } },
    {
        value: "centered-accent",
        label: { ar: "موسّط بلمسة", en: "Centered accent" },
    },
    {
        value: "rounded-shadow",
        label: { ar: "ظل ناعم", en: "Rounded shadow" },
    },
    { value: "list-row", label: { ar: "صف أفقي", en: "List row" } },
    { value: "bordered", label: { ar: "محدد", en: "Bordered" } },
    { value: "framed", label: { ar: "إطار", en: "Framed" } },
    { value: "bold-tile", label: { ar: "بلاطة جريئة", en: "Bold tile" } },
    { value: "catalog", label: { ar: "كتالوج", en: "Catalog" } },
    { value: "showcase", label: { ar: "عرض", en: "Showcase" } },
    { value: "gamer", label: { ar: "ألعاب", en: "Gamer" } },
];
/**
 * Reveal-engine choices for the theme-level scroll animation. Mirrors the two
 * EasyOrders engines from the animation audit:
 * - `uniform` — the `ab-*` engine (one shared fade-up per section).
 * - `cycle`   — the `ps-*` engine (sections cycle through varied motions).
 * Single source of truth, reused by the seller ThemeStep + admin studio.
 */
export const SECTION_REVEAL_STYLE_OPTIONS = [
    { value: "uniform", label: { ar: "موحّدة", en: "Uniform" } },
    { value: "cycle", label: { ar: "متنوّعة", en: "Varied" } },
];
/**
 * The page-gutter choices. These are the MEASURED `content_container` insets of
 * the audited EasyOrders families (sharp 0, phoenix 10, nature 16, hype/pearl/
 * peral 24, elite/avyro 32, volta 48, prime 50, blast 52) plus the platform
 * default — not a made-up ramp. Empty = the platform default
 * `clamp(20px, 5vw, 80px)`, which is what every pre-existing store renders.
 */
export const PAGE_GUTTER_OPTIONS = [
    { value: "0", label: { ar: "بدون (عرض كامل)", en: "None (full-bleed)" } },
    { value: "10px", label: { ar: "١٠ بكسل", en: "10px" } },
    { value: "16px", label: { ar: "١٦ بكسل", en: "16px" } },
    { value: "24px", label: { ar: "٢٤ بكسل", en: "24px" } },
    { value: "32px", label: { ar: "٣٢ بكسل", en: "32px" } },
    { value: "48px", label: { ar: "٤٨ بكسل", en: "48px" } },
    { value: "50px", label: { ar: "٥٠ بكسل", en: "50px" } },
    { value: "52px", label: { ar: "٥٢ بكسل", en: "52px" } },
    { value: "72px", label: { ar: "٧٢ بكسل", en: "72px" } },
];
const layoutFields = [
    // ONE container for the whole storefront. See `ThemeTokens.pageGutter`.
    {
        id: "pageGutter",
        type: "select",
        label: { ar: "هامش الصفحة", en: "Page gutter" },
        helpText: {
            ar: "المسافة الجانبية لكل أقسام المتجر. سيبه فاضي للقيمة الافتراضية.",
            en: "The side inset every storefront section shares. Leave empty for the platform default.",
        },
        options: PAGE_GUTTER_OPTIONS,
    },
    {
        id: "pageGutterMobile",
        type: "select",
        label: { ar: "هامش الصفحة (موبايل)", en: "Page gutter (mobile)" },
        helpText: {
            ar: "سيبه فاضي عشان يتحسب من هامش الصفحة.",
            en: "Leave empty to derive it from the desktop gutter.",
        },
        options: PAGE_GUTTER_OPTIONS,
        showIf: { pageGutter: PAGE_GUTTER_OPTIONS.map((o) => o.value) },
    },
    // See `ThemeTokens.heroBleed` — the hero is the one band a family may want
    // OUTSIDE the container it just declared.
    {
        id: "heroBleed",
        type: "checkbox",
        label: { ar: "الهيرو بعرض الشاشة", en: "Full-bleed hero" },
        helpText: {
            ar: "الهيرو يمتد لحواف الشاشة حتى لو باقي الأقسام ليها هامش.",
            en: "The hero runs edge-to-edge even when the rest of the page has a gutter.",
        },
        showIf: { pageGutter: PAGE_GUTTER_OPTIONS.map((o) => o.value) },
    },
    // The token→role binding for headings. See `ThemeTokens.headingRole`.
    {
        id: "headingRole",
        type: "select",
        label: { ar: "لون العناوين", en: "Heading color" },
        helpText: {
            ar: "العناوين بتاخد لون النص أو اللون الأساسي أو المميز.",
            en: "Which palette role paints section headings and product names.",
        },
        options: [
            { value: "ink", label: { ar: "لون النص", en: "Text color" } },
            { value: "primary", label: { ar: "اللون الأساسي", en: "Primary" } },
            { value: "accent", label: { ar: "اللون المميز", en: "Accent" } },
        ],
        default: "ink",
    },
    {
        id: "radius",
        type: "select",
        label: { ar: "حواف العناصر", en: "Corner radius" },
        helpText: {
            ar: "درجة استدارة حواف الأزرار والكروت.",
            en: "How rounded buttons and cards appear.",
        },
        options: [
            { value: "0", label: { ar: "حادة", en: "Sharp" } },
            { value: "0.25rem", label: { ar: "خفيفة", en: "Subtle" } },
            { value: "0.5rem", label: { ar: "متوسطة", en: "Medium" } },
            { value: "0.75rem", label: { ar: "كبيرة", en: "Large" } },
            { value: "1rem", label: { ar: "كبيرة جدًا", en: "Extra large" } },
        ],
        default: "0.5rem",
    },
    {
        id: "logoTreatment",
        type: "select",
        label: { ar: "شكل اللوجو", en: "Logo treatment" },
        helpText: {
            ar: "إطار اللوجو في الشريط العلوي والواجهة الرئيسية.",
            en: "Frame for the logo in the header and hero.",
        },
        options: [
            { value: "square", label: { ar: "مربع", en: "Square" } },
            { value: "circle", label: { ar: "دائري", en: "Circle" } },
            { value: "none", label: { ar: "بدون", en: "No frame" } },
        ],
        default: "circle",
    },
    {
        id: "productCardStyle",
        type: "select",
        ui: "thumbnail",
        label: { ar: "شكل كارت المنتج", en: "Product card style" },
        helpText: {
            ar: "بيتطبق على كل كروت المنتجات في المتجر.",
            en: "Applied to every product card across the storefront.",
        },
        options: PRODUCT_CARD_STYLE_OPTIONS,
        default: "grid",
    },
    {
        id: "productCardRadius",
        type: "select",
        label: { ar: "حواف كارت المنتج", en: "Product card radius" },
        helpText: {
            ar: "درجة استدارة حواف صورة كارت المنتج.",
            en: "How rounded the product-card image corners are.",
        },
        options: [
            { value: "0", label: { ar: "حادة", en: "Sharp" } },
            { value: "0.5rem", label: { ar: "صغيرة", en: "Small" } },
            { value: "0.75rem", label: { ar: "متوسطة", en: "Medium" } },
            { value: "1rem", label: { ar: "كبيرة", en: "Large" } },
            { value: "1.5rem", label: { ar: "كبيرة جدًا", en: "Extra large" } },
        ],
        default: "0",
    },
    {
        id: "productCardShadow",
        type: "select",
        label: { ar: "ظل كارت المنتج", en: "Product card shadow" },
        helpText: {
            ar: "قوة الظل تحت كارت المنتج.",
            en: "Drop-shadow depth under the product card.",
        },
        options: [
            { value: "none", label: { ar: "بدون", en: "None" } },
            { value: "sm", label: { ar: "خفيف", en: "Subtle" } },
            { value: "md", label: { ar: "متوسط", en: "Medium" } },
            { value: "lg", label: { ar: "قوي", en: "Large" } },
        ],
        default: "none",
    },
    {
        id: "productCardImageFit",
        type: "select",
        label: { ar: "ملء صورة المنتج", en: "Product image fit" },
        helpText: {
            ar: "تغطية: تملأ الإطار مع قص. احتواء: تظهر الصورة كاملة.",
            en: "Cover fills + crops; contain shows the whole image.",
        },
        options: [
            { value: "cover", label: { ar: "تغطية", en: "Cover" } },
            { value: "contain", label: { ar: "احتواء", en: "Contain" } },
        ],
        default: "cover",
    },
    {
        id: "productCardImageBg",
        type: "color",
        label: { ar: "خلفية صورة المنتج", en: "Product image background" },
        helpText: {
            ar: 'لون الفراغ ورا الصورة لما تختار "احتواء" — مفيد للصور بخلفية غامقة.',
            en: 'Fills the letterbox area behind "contain" images — useful for dark-background photos.',
        },
        showIf: { productCardImageFit: "contain" },
    },
    {
        id: "productCardShowColors",
        type: "checkbox",
        label: { ar: "عرض ألوان المنتج", en: "Show color swatches" },
        helpText: {
            ar: "يعرض دوائر صغيرة بألوان المنتج المتاحة على الكارت.",
            en: "Shows small dots of the product's available colors on the card.",
        },
        default: false,
    },
    // --- "Card" style only: the always-visible add-to-order button ----------
    {
        id: "productCardShowAddButton",
        type: "checkbox",
        label: { ar: "عرض زر الإضافة للطلب", en: "Show add-to-order button" },
        helpText: {
            ar: 'يظهر زر الإضافة أسفل كل كارت في شكل "بطاقة مع زر".',
            en: "Shows the add button under every card in the Card style.",
        },
        default: true,
        showIf: { productCardStyle: "card" },
    },
    {
        id: "productCardAddButtonStyle",
        type: "select",
        label: { ar: "شكل زر الإضافة", en: "Add button style" },
        options: [
            { value: "outline", label: { ar: "محدد", en: "Outline" } },
            { value: "solid", label: { ar: "ممتلئ", en: "Filled" } },
        ],
        default: "outline",
        showIf: { productCardStyle: "card", productCardShowAddButton: true },
    },
    {
        id: "productCardAddButtonRadius",
        type: "select",
        label: { ar: "حواف زر الإضافة", en: "Add button radius" },
        options: [
            { value: "0", label: { ar: "حادة", en: "Sharp" } },
            { value: "6px", label: { ar: "صغيرة", en: "Small" } },
            { value: "10px", label: { ar: "متوسطة", en: "Medium" } },
            { value: "16px", label: { ar: "كبيرة", en: "Large" } },
            { value: "9999px", label: { ar: "حبة", en: "Pill" } },
        ],
        default: "9999px",
        showIf: { productCardStyle: "card", productCardShowAddButton: true },
    },
    {
        id: "productCardAddButtonLabel",
        type: "text",
        i18n: true,
        label: { ar: "نص زر الإضافة", en: "Add button label" },
        helpText: {
            ar: "سيبه فاضي لاستخدام النص الافتراضي.",
            en: "Leave empty to use the default label.",
        },
        // Surfaced as the input placeholder so the seller sees the current
        // default; the stored value stays empty until they type an override
        // (empty → the buyer's own localized "Add to order").
        default: { ar: "ضيف على الطلب", en: "Add to order" },
        showIf: { productCardStyle: "card", productCardShowAddButton: true },
    },
    // --- Motion: theme-level scroll-reveal (whole-page entrance animation) -----
    // Off by default so every store authored before this field stays static.
    {
        id: "sectionReveal",
        type: "checkbox",
        label: { ar: "تحريك الأقسام مع التمرير", en: "Reveal sections on scroll" },
        helpText: {
            ar: "كل قسم بيظهر بحركة ناعمة أول ما يوصله المستخدم بالتمرير.",
            en: "Each section fades / slides in as it scrolls into view.",
        },
        default: false,
    },
    {
        id: "sectionRevealStyle",
        type: "select",
        label: { ar: "نوع الحركة", en: "Reveal motion" },
        helpText: {
            ar: "موحّدة: نفس الحركة لكل الأقسام. متنوّعة: كل قسم بحركة مختلفة.",
            en: "Uniform: one shared fade-up. Varied: each section a different motion.",
        },
        options: SECTION_REVEAL_STYLE_OPTIONS,
        default: "uniform",
        showIf: { sectionReveal: true },
    },
    {
        id: "sectionRevealSpeed",
        type: "range",
        label: { ar: "سرعة الحركة", en: "Reveal speed" },
        helpText: {
            ar: "مدة الحركة بالمللي ثانية.",
            en: "Entrance duration in milliseconds.",
        },
        min: 300,
        max: 1000,
        step: 50,
        default: 600,
        showIf: { sectionReveal: true },
    },
];
const buttonFields = [
    {
        id: "buttonStyle",
        type: "select",
        label: { ar: "نمط الزر", en: "Button style" },
        helpText: {
            ar: "شكل الأزرار الأساسية: ممتلئ، محدد، شفاف، أو رابط.",
            en: "Primary button appearance: solid, outline, ghost, or link.",
        },
        options: [
            { value: "solid", label: { ar: "ممتلئ", en: "Solid" } },
            { value: "outline", label: { ar: "محدد", en: "Outline" } },
            { value: "ghost", label: { ar: "شفاف", en: "Ghost" } },
            { value: "link", label: { ar: "رابط", en: "Link" } },
        ],
        default: "solid",
    },
    {
        id: "buttonBackground",
        type: "color",
        label: { ar: "خلفية الزر", en: "Button background" },
        helpText: {
            ar: "لون خلفية الأزرار الأساسية. سيبه فاضي عشان ياخد اللون الأساسي.",
            en: "Background color for primary buttons. Leave empty to use the primary color.",
        },
        default: "",
    },
    {
        id: "buttonTextColor",
        type: "color",
        label: { ar: "لون نص الزر", en: "Button text color" },
        helpText: {
            ar: "لون نص الأزرار الأساسية. سيبه فاضي للون الافتراضي.",
            en: "Text color for primary buttons. Leave empty for the default.",
        },
        default: "",
    },
    {
        id: "buttonRadius",
        type: "select",
        label: { ar: "حواف الزر", en: "Button radius" },
        helpText: {
            ar: "درجة استدارة حواف الأزرار.",
            en: "How rounded buttons are.",
        },
        options: [
            { value: "0", label: { ar: "حادة", en: "Sharp" } },
            { value: "6px", label: { ar: "صغيرة", en: "Small" } },
            { value: "10px", label: { ar: "متوسطة", en: "Medium" } },
            { value: "16px", label: { ar: "كبيرة", en: "Large" } },
            { value: "9999px", label: { ar: "حبة", en: "Pill" } },
        ],
        default: "10px",
    },
    {
        id: "buttonSize",
        type: "select",
        label: { ar: "حجم الزر", en: "Button size" },
        helpText: {
            ar: "ارتفاع الأزرار وحجم النص.",
            en: "Button height and text size.",
        },
        options: [
            { value: "sm", label: { ar: "صغير", en: "Small" } },
            { value: "md", label: { ar: "متوسط", en: "Medium" } },
            { value: "lg", label: { ar: "كبير", en: "Large" } },
        ],
        default: "md",
    },
];
export const themeSchema = {
    colors: colorFields,
    typography: typographyFields,
    layout: layoutFields,
    buttons: buttonFields,
};
//# sourceMappingURL=theme.js.map